import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { AuthBrand } from '../components/AuthBrand';
import { PersonIcon } from '../components/AuthIcons';
import { AuthField } from '../components/AuthField';
import { AuthSplitCopy } from '../components/AuthSplitCopy';
import { AuthSplitDecor } from '../components/AuthSplitDecor';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ThemeToggle } from '../components/ThemeToggle';
import { useLanguage } from '../lib/i18n/LanguageContext';

type Status = 'verifying' | 'success' | 'failed' | 'missing-token';

/**
 * Lands from the link emailed by POST /auth/register or .../resend.
 *
 * Deliberately never establishes a session on success, even though the
 * caller is right there having just proven ownership of the address:
 * /auth/login is the only place a session is issued, so there is exactly one
 * code path to reason about instead of two that must stay in sync.
 *
 * The one exception is `requires_two_factor_setup`: a self-service b2b org
 * owner/admin has mandatory TOTP and starts with zero factors, so without a
 * session issued right here there would be no way to ever reach an
 * authenticated session and enroll one — see LLMR-b2b-self-signup's
 * first-login deadlock fix. `onTwoFactorSetupRequired` tells App.tsx a
 * session now exists so it can flip its top-level state; this component
 * then redirects into the signed-in tree itself so App's
 * 'two-factor-setup-required' branch (a catch-all route) takes over,
 * instead of staying on this "email verified" screen.
 */
export function VerifyEmail({ onTwoFactorSetupRequired }: { onTwoFactorSetupRequired?: () => void }) {
  const { dict } = useLanguage();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'missing-token');
  const [twoFactorSetupRequired, setTwoFactorSetupRequired] = useState(false);
  // Only set when the signup form's (optional) promo code redeemed
  // successfully — see llmr-control-api's own comment on why the amount is
  // never known before this exact moment.
  const [promoCredit, setPromoCredit] = useState<{ amount: string; currency: string } | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState<'idle' | 'busy' | 'sent'>('idle');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void api
      .verifyEmail(token)
      .then((res) => {
        if (cancelled) return;
        if (res.promo_credit) setPromoCredit(res.promo_credit);
        if (res.requires_two_factor_setup) {
          setTwoFactorSetupRequired(true);
          onTwoFactorSetupRequired?.();
        } else {
          setStatus('success');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [token, onTwoFactorSetupRequired]);

  if (twoFactorSetupRequired) {
    return <Navigate to="/dashboard" replace />;
  }

  async function resend() {
    setResendState('busy');
    try {
      await api.resendVerification(resendEmail);
    } finally {
      setResendState('sent');
    }
  }

  return (
    <div className="auth-screen auth-split">
      <div className="auth-theme-toggle">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <AuthBrand />

      <div className="auth-split-panel auth-split-panel--signup">
        <AuthSplitDecor />
        <AuthSplitCopy
          eyebrow={dict.verifyEmail.welcomeEyebrow}
          headline={dict.verifyEmail.welcomeHeadline}
          body={dict.verifyEmail.welcomeBody}
        />

        <div className="auth-split-form">
          <div className="auth-split-form-inner">
            {status === 'verifying' && (
              <>
                <h1>{dict.verifyEmail.verifyingTitle}</h1>
                <p className="auth-lede">{dict.verifyEmail.verifyingBody}</p>
              </>
            )}

            {status === 'success' && (
              <>
                <h1>{dict.verifyEmail.successTitle}</h1>
                <p className="auth-lede">{dict.verifyEmail.successBody}</p>
                {promoCredit && <p className="note">{dict.verifyEmail.promoCredited(promoCredit.amount)}</p>}
                <Link to="/login" className="btn-block">
                  {dict.verifyEmail.goToSignIn}
                </Link>
              </>
            )}

            {(status === 'failed' || status === 'missing-token') && (
              <>
                <h1>{dict.verifyEmail.failedTitle}</h1>
                <p className="auth-lede">
                  {status === 'missing-token' ? dict.verifyEmail.missingTokenBody : dict.verifyEmail.failedBody}
                </p>

                <AuthField
                  id="resendEmail"
                  label={dict.verifyEmail.resendEmailLabel}
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  autoComplete="email"
                  trailing={<PersonIcon />}
                />
                <button
                  type="button"
                  className="btn-block"
                  disabled={resendState === 'busy' || !resendEmail}
                  onClick={() => void resend()}
                >
                  {resendState === 'busy'
                    ? dict.verifyEmail.resendBusy
                    : resendState === 'sent'
                      ? dict.verifyEmail.resendSent
                      : dict.verifyEmail.resendButton}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
